import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_YEAR_TARGET, UPSERT_YEAR_TARGET, ADD_MILESTONE, ADD_GOAL,
  UPDATE_GOAL, DELETE_GOAL, DELETE_MILESTONE
} from '../graphql';
import {
  Target, Plus, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight,
  Trash2, Edit3, Flag, Calendar, TrendingUp, Award, BookOpen, Briefcase, Zap, Heart,
  Check
} from 'lucide-react';

const CATEGORY_META = {
  ACADEMIC: { label: 'Academic', color: '#1a73e8', bg: '#EFF6FF', icon: BookOpen },
  CAREER:   { label: 'Career',   color: '#1257a0', bg: '#FAF6EE', icon: Briefcase },
  SKILL:    { label: 'Skill',    color: '#7c3aed', bg: '#F5F3FF', icon: Zap },
  PERSONAL: { label: 'Personal', color: '#b06000', bg: '#FAF7F1', icon: Heart },
};

const PRIORITY_META = {
  HIGH:   { label: 'High',   color: '#dc2626', bg: '#FEF2F2' },
  MEDIUM: { label: 'Medium', color: '#b06000', bg: '#FAF7F1' },
  LOW:    { label: 'Low',    color: '#188038', bg: '#EEF4EC' },
};

const STATUS_CYCLE = { PENDING: 'IN_PROGRESS', IN_PROGRESS: 'COMPLETED', COMPLETED: 'PENDING', MISSED: 'IN_PROGRESS' };

function ProgressRing({ pct, size = 80, stroke = 7, color = '#1a73e8' }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.5s ease' }} />
    </svg>
  );
}

function GoalRow({ goal, onStatusChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const cat = CATEGORY_META[goal.category] || CATEGORY_META.ACADEMIC;
  const pri = PRIORITY_META[goal.priority] || PRIORITY_META.MEDIUM;
  const CatIcon = cat.icon;
  const isOverdue = goal.isOverdue;
  const isDone = goal.status === 'COMPLETED';

  return (
    <div className={`rounded-xl border transition-all ${
      isOverdue ? 'border-red-200 bg-red-50/60' : 'border-gray-100 bg-white'
    }`}>
      <div className="flex items-start gap-3 p-3">
        <button
          onClick={() => onStatusChange(goal.id, STATUS_CYCLE[goal.status])}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
            isDone ? 'bg-green-500 border-green-500' :
            goal.status === 'IN_PROGRESS' ? 'border-brand-500 bg-brand-50' :
            isOverdue ? 'border-red-400 bg-red-50' :
            'border-gray-300 hover:border-brand-400'
          }`}
        >
          {isDone && <Check size={11} className="text-white" />}
          {goal.status === 'IN_PROGRESS' && <div className="w-2 h-2 rounded-full bg-brand-500" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`text-sm font-medium ${isDone ? 'line-through text-gray-400' : isOverdue ? 'text-red-700' : 'text-gray-900'}`}>
              {goal.title}
            </span>
            {isOverdue && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                <AlertTriangle size={10} /> Overdue
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md font-medium"
              style={{ background: cat.bg, color: cat.color }}>
              <CatIcon size={10} /> {cat.label}
            </span>
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md font-medium"
              style={{ background: pri.bg, color: pri.color }}>
              <Flag size={9} /> {pri.label}
            </span>
            {goal.dueDate && (
              <span className={`inline-flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                <Calendar size={10} /> {new Date(goal.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          {goal.description && expanded && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">{goal.description}</p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {goal.description && (
            <button onClick={() => setExpanded(e => !e)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
              {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          )}
          <button onClick={() => onDelete(goal.id)}
            className="p-1 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function AddGoalForm({ milestoneId, onDone }) {
  const [title, setTitle]       = useState('');
  const [desc, setDesc]         = useState('');
  const [category, setCategory] = useState('ACADEMIC');
  const [priority, setPriority] = useState('MEDIUM');
  const [dueDate, setDueDate]   = useState('');
  const [addGoal, { loading }]  = useMutation(ADD_GOAL, { refetchQueries: ['MyYearTarget'] });

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await addGoal({ variables: { milestoneId, title: title.trim(), description: desc.trim() || null, category, priority, dueDate: dueDate || null } });
    onDone();
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-dashed border-brand-200 bg-brand-50/40 p-3 space-y-2.5">
      <input
        autoFocus
        value={title} onChange={e => setTitle(e.target.value)}
        placeholder="Goal title…"
        className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400 text-gray-900 placeholder-gray-400"
      />
      <textarea
        value={desc} onChange={e => setDesc(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="w-full text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400 text-gray-900 placeholder-gray-400 resize-none"
      />
      <div className="grid grid-cols-3 gap-2">
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-2 focus:outline-none text-gray-700">
          {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priority} onChange={e => setPriority(e.target.value)}
          className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-2 focus:outline-none text-gray-700">
          {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
          className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-2 focus:outline-none text-gray-700" />
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone}
          className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-all">
          Cancel
        </button>
        <button type="submit" disabled={loading || !title.trim()}
          className="text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 px-4 py-1.5 rounded-lg transition-all">
          {loading ? 'Adding…' : 'Add Goal'}
        </button>
      </div>
    </form>
  );
}

function MilestoneCard({ milestone, onDeleteMilestone }) {
  const [addingGoal, setAddingGoal] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [updateGoal] = useMutation(UPDATE_GOAL, { refetchQueries: ['MyYearTarget'] });
  const [deleteGoal] = useMutation(DELETE_GOAL, { refetchQueries: ['MyYearTarget'] });

  const hasOverdue = milestone.overdueCount > 0;
  const headerColor = hasOverdue ? '#dc2626' :
    milestone.progress === 100 ? '#188038' :
    milestone.progress > 0 ? '#1a73e8' : '#6b7280';

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${hasOverdue ? 'border-red-200' : 'border-gray-100'}`}>
      <div className="flex items-center gap-3 px-4 py-3"
        style={{ background: hasOverdue ? '#FEF2F2' : milestone.progress === 100 ? '#EEF4EC' : '#F8FAFF' }}>
        <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: headerColor }}>
            Y{milestone.yearNum}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-gray-900">{milestone.label}</span>
              {hasOverdue && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                  <AlertTriangle size={10} /> {milestone.overdueCount} overdue
                </span>
              )}
              {milestone.progress === 100 && !hasOverdue && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <Award size={10} /> Complete
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden max-w-[120px]">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${milestone.progress}%`, background: headerColor }} />
              </div>
              <span className="text-xs text-gray-400">{milestone.progress}% · {milestone.goals.length} goals</span>
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-all">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <button onClick={() => onDeleteMilestone(milestone.id)}
            className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-3 space-y-2 bg-white">
          {milestone.goals.length === 0 && !addingGoal && (
            <p className="text-xs text-gray-400 text-center py-3">No goals yet — add one below</p>
          )}
          {[...milestone.goals].sort((a, b) => (b.isOverdue ? 1 : 0) - (a.isOverdue ? 1 : 0)).map(goal => (
            <GoalRow
              key={goal.id}
              goal={goal}
              onStatusChange={(id, status) => updateGoal({ variables: { goalId: id, status } })}
              onDelete={(id) => deleteGoal({ variables: { goalId: id } })}
            />
          ))}
          {addingGoal && <AddGoalForm milestoneId={milestone.id} onDone={() => setAddingGoal(false)} />}
          {!addingGoal && (
            <button onClick={() => setAddingGoal(true)}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50 py-2 rounded-lg border border-dashed border-brand-200 transition-all">
              <Plus size={12} /> Add goal
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SetupForm({ onSaved }) {
  const currentYear = new Date().getFullYear();
  const [title, setTitle]           = useState('My 4-Year College Vision');
  const [vision, setVision]         = useState('');
  const [startYear, setStartYear]   = useState(currentYear);
  const [targetYear, setTargetYear] = useState(currentYear + 4);
  const [upsert, { loading }]       = useMutation(UPSERT_YEAR_TARGET, { refetchQueries: ['MyYearTarget'] });

  const submit = async (e) => {
    e.preventDefault();
    await upsert({ variables: { title, vision: vision.trim() || null, startYear, targetYear } });
    onSaved();
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
          <Target size={28} className="text-brand-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set your 4-year vision</h2>
        <p className="text-gray-500 text-sm">Define where you want to be by graduation and track every step.</p>
      </div>
      <form onSubmit={submit} className="space-y-4 bg-white rounded-2xl border border-gray-100 p-6">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Vision title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} required
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-brand-400"
            placeholder="My 4-Year College Vision" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Vision statement</label>
          <textarea value={vision} onChange={e => setVision(e.target.value)} rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-brand-400 resize-none"
            placeholder="In 4 years, I want to be placed at a top tech company with a strong CGPA, a portfolio of 3 real projects, and a network that lasts a lifetime." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Start year</label>
            <input type="number" value={startYear} onChange={e => setStartYear(Number(e.target.value))} required min={2020} max={2040}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-brand-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Target year</label>
            <input type="number" value={targetYear} onChange={e => setTargetYear(Number(e.target.value))} required min={2021} max={2045}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-brand-400" />
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="w-full h-11 rounded-xl font-semibold text-white text-sm bg-brand-500 hover:bg-brand-600 disabled:opacity-50 transition-colors">
          {loading ? 'Creating…' : 'Create My Vision Board'}
        </button>
      </form>
    </div>
  );
}

export default function YearTargetPage() {
  const { data, loading, error } = useQuery(MY_YEAR_TARGET);
  const [showSetup, setShowSetup]         = useState(false);
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [newMsYear, setNewMsYear]         = useState(1);
  const [newMsLabel, setNewMsLabel]       = useState('');
  const [addMilestone, { loading: addingMs }] = useMutation(ADD_MILESTONE, { refetchQueries: ['MyYearTarget'] });
  const [deleteMilestone]                 = useMutation(DELETE_MILESTONE, { refetchQueries: ['MyYearTarget'] });

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-10 text-center text-sm text-gray-400">Loading your vision board…</div>
  );
  if (error) return (
    <div className="max-w-3xl mx-auto px-4 py-10 text-center text-sm text-red-500">Failed to load. Please refresh.</div>
  );

  const target = data?.myYearTarget;

  if (!target || showSetup) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <SetupForm onSaved={() => setShowSetup(false)} />
      </div>
    );
  }

  const submitMilestone = async (e) => {
    e.preventDefault();
    if (!newMsLabel.trim()) return;
    await addMilestone({ variables: { targetId: target.id, yearNum: newMsYear, label: newMsLabel.trim() } });
    setAddingMilestone(false); setNewMsLabel(''); setNewMsYear(1);
  };

  const progressColor = target.overdueGoals > 0 ? '#dc2626' : target.overallProgress >= 80 ? '#188038' : '#1a73e8';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="relative flex-shrink-0">
          <ProgressRing pct={target.overallProgress} size={76} stroke={6} color={progressColor} />
          <span className="absolute inset-0 flex items-center justify-center text-sm font-bold"
            style={{ color: progressColor }}>
            {target.overallProgress}%
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-snug">{target.title}</h1>
              <p className="text-xs text-gray-400 mt-0.5">{target.startYear} – {target.targetYear}</p>
            </div>
            <button onClick={() => setShowSetup(true)}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
              <Edit3 size={15} />
            </button>
          </div>
          {target.vision && (
            <p className="text-sm text-gray-500 mt-2 leading-relaxed italic">"{target.vision}"</p>
          )}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <CheckCircle2 size={13} className="text-green-500" /> {target.completedGoals}/{target.totalGoals} done
            </span>
            {target.overdueGoals > 0 && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-red-600">
                <AlertTriangle size={13} /> {target.overdueGoals} overdue
              </span>
            )}
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <TrendingUp size={13} /> {target.milestones.length} milestones
            </span>
          </div>
        </div>
      </div>

      {/* Overdue banner */}
      {target.overdueGoals > 0 && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">
              {target.overdueGoals} goal{target.overdueGoals > 1 ? 's' : ''} past their deadline
            </p>
            <p className="text-xs text-red-500 mt-0.5">Review and update your progress or adjust the due dates.</p>
          </div>
        </div>
      )}

      {/* Milestones */}
      <div className="space-y-4">
        {target.milestones.map(m => (
          <MilestoneCard
            key={m.id}
            milestone={m}
            onDeleteMilestone={(id) => deleteMilestone({ variables: { milestoneId: id } })}
          />
        ))}
      </div>

      {/* Add milestone */}
      <div className="mt-4">
        {addingMilestone ? (
          <form onSubmit={submitMilestone}
            className="rounded-2xl border border-dashed border-brand-200 bg-brand-50/30 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Add a milestone year</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Year number</label>
                <input type="number" value={newMsYear} onChange={e => setNewMsYear(Number(e.target.value))} min={1} max={6}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Label</label>
                <input value={newMsLabel} onChange={e => setNewMsLabel(e.target.value)} required autoFocus
                  placeholder="e.g. First Year"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-400" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setAddingMilestone(false)}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-all">
                Cancel
              </button>
              <button type="submit" disabled={addingMs || !newMsLabel.trim()}
                className="text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 px-4 py-1.5 rounded-lg transition-all">
                {addingMs ? 'Adding…' : 'Add Milestone'}
              </button>
            </div>
          </form>
        ) : (
          <button onClick={() => setAddingMilestone(true)}
            className="w-full flex items-center justify-center gap-2 text-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50 py-3 rounded-2xl border border-dashed border-brand-200 transition-all font-medium">
            <Plus size={15} /> Add year milestone
          </button>
        )}
      </div>
    </div>
  );
}
